import type { LucideIcon } from "lucide-react";

import type { GenerationType, Job } from "../../../api/generated/model";
import { GENERATIONS_PAGE, useJobList } from "../../../platform/jobs/job-list";
import { useStreamStatus } from "../../../platform/stream-status";
import { CardPicture } from "../../../ui/CardPicture";
import classes from "./simple-home.module.css";

/** A card's kind of result, each the filter its composer's results read (Pro's and Simple's). */
export const TALKING_TYPES = ["video.talking_head", "video.talking_head_advanced"] as const;
export const AD_STATIC_TYPES = ["image.advanced"] as const;
export const PRODUCT_VIDEO_TYPES = ["video.image"] as const;
/**
 * The Images page's own (spec ui-perf/simple-images): the guided page's Image, a tweak once it has
 * a product photo or a reference, and the actors made.
 */
export const PRODUCT_PHOTO_TYPES = ["image.simple", "image.tweak"] as const;
export const ACTOR_TYPES = ["image.actor"] as const;

export interface Picture {
  /** An image's thumbnail or a video's poster: never the file itself. */
  src: string;
  /** A video's poster, whose faces sit in its upper third. */
  video: boolean;
}

/** A finished job's first picture with a file still kept, else none. */
export function pictureOf(job: Job): Picture | undefined {
  if (job.status !== "succeeded") return undefined;
  const video = job.type.startsWith("video.");
  for (const output of job.outputs) {
    // A file past its days and never saved, or one that failed, has no picture to show.
    if (output.url === null) continue;
    const src = video ? output.poster_url : output.thumb_url;
    if (src != null) return { src, video };
  }
  return undefined;
}

/**
 * The brand's newest pictures of these kinds, from the read the composers' results make here (5 a
 * page, the same key), so Home and a composer share one cache (spec ui-perf/simple-flows).
 */
export function useOwnPictures(teamId: string, brandId: string, types: readonly GenerationType[]) {
  const jobs = useJobList(
    teamId,
    { status: "all", brandId, types, pageSize: GENERATIONS_PAGE },
    useStreamStatus(),
  );
  const pictures = (jobs.data?.pages[0]?.items ?? []).flatMap((job) => {
    const picture = pictureOf(job);
    return picture === undefined ? [] : [picture];
  });
  // A picture's signed URL that fails reads the list again, as Continue's do.
  return { pictures, readAgain: () => void jobs.refetch() };
}

/**
 * A picture, else the card's icon on the pane colour. A picture that fails to load keeps its place,
 * unseen under the icon, until it loads after all: the media cookies' retry asks again for that
 * very element (web/AGENTS.md; `ui/CardPicture`), and the list is read again for fresh URLs.
 */
export function Art(props: { picture: Picture | undefined; icon: LucideIcon; onLost: () => void }) {
  const { picture, icon: Icon, onLost } = props;
  const icon = (
    <span className={classes.artIcon}>
      <Icon size={28} strokeWidth={1.5} aria-hidden />
    </span>
  );
  if (picture === undefined) return icon;
  return (
    <span className={classes.art} data-video={picture.video || undefined}>
      <CardPicture src={picture.src} alt="" onFailed={onLost} fallback={icon} />
    </span>
  );
}
